import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, Modal } from "antd";
import { IntegrationOrderType } from "@/types/integration";
import { Cube2BOrder, Cube2COrder, Cube2COrderInstructionState, DialogRef } from "@/types";
import { query2BOrders, query2COrders } from "@/apis/cube/order";
import { deleteOrder } from "@/apis/integration/order";
import { useGlobalContext } from "@/hooks/context";
import Indicator from "@/components/shared/Indicator";
import { CopyOutlined, LinkOutlined } from "@ant-design/icons";

interface DeleteOrderButtonProps {
  className?: string;
}

export const DeleteOrderButton = forwardRef<DialogRef, DeleteOrderButtonProps>(
  (props, ref) => {
    const { className } = props;
    const { hintPrompt, copyText } = useGlobalContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [customerOrderNo, setCustomerOrderNo] = useState<string | undefined>(
      undefined,
    );
    const orderIdRef = useRef<number | undefined>(undefined);
    const cubeOrderIdRef = useRef<string | undefined>(undefined);
    const deleteCbRef = useRef<() => void | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [existingCubeOrder, setExistingCubeOrder] = useState<Cube2COrder | Cube2BOrder | undefined>(undefined);

    const deletable = useMemo(() => {
      if (existingCubeOrder == null) return true;
      if ('instruction' in existingCubeOrder) {
        return [
          Cube2COrderInstructionState.REFUNDED_SUCCESS,
          Cube2COrderInstructionState.REFUND_TIMEOUT_AMOUNT_REFUND,
          Cube2COrderInstructionState.REFUND_TIMEOUT_NOT_REFUND,
        ].includes(existingCubeOrder.instruction.state);
      }
    }, [existingCubeOrder])

    const checkCubeOrderExist = useCallback(
      async (type: IntegrationOrderType, customerOrderNo: string) => {
        setLoading(true);
        let promise: Promise<Cube2COrder | Cube2BOrder> | undefined;
        if (type === IntegrationOrderType.Business) {
          promise = query2BOrders({
            customerNumber: customerOrderNo,
          }).then(res => {
            res.items = res.items.filter(
              item => item.boxStockDeliverId === `${cubeOrderIdRef.current}`
            );
            return res.items?.[0] ?? undefined;
          });
        } else {
          promise = query2COrders({
            theThirdNumber: customerOrderNo,
          }).then(res => {
            res.items = res.items.filter(
              item => item.instruction.instructionId === `${cubeOrderIdRef.current}`
            );
            return res.items?.[0] ?? undefined;
          })
        }
        promise
          ?.then(setExistingCubeOrder)
          .catch(() => {
            setExistingCubeOrder(undefined);
            hintPrompt("error", "Failed to check cube order");
          })
          .finally(() => {
            setLoading(false);
          });
        return promise;
      },
      [hintPrompt],
    );

    const closeDialog = useCallback(() => {
      deleteCbRef.current?.();
      setIsModalOpen(false);
      setCustomerOrderNo(undefined);
      orderIdRef.current = undefined;
      cubeOrderIdRef.current = undefined;
      deleteCbRef.current = undefined;
      setExistingCubeOrder(undefined);
    }, []);

    useImperativeHandle(ref, () => ({
      showModal: (props) => {
        const { orderId, cubeOrderId, type, customerOrderNo, deleteCb } = props as {
          orderId: number;
          cubeOrderId: string;
          type: IntegrationOrderType;
          customerOrderNo: string;
          deleteCb?: () => void;
        };
        if (!orderId || !type || !customerOrderNo) return;
        setIsModalOpen(true);
        orderIdRef.current = orderId;
        cubeOrderIdRef.current = cubeOrderId;
        deleteCbRef.current = deleteCb;
        setCustomerOrderNo(customerOrderNo);
        checkCubeOrderExist(type, customerOrderNo);
      },
      hideModal: closeDialog,
    }));

    const handleOk = useCallback(
      (orderId?: number, hard?: boolean) => {
        if (!orderId) return;
        setLoading(true);
        deleteOrder({ orderId, hard })
          .then(() => {
            hintPrompt("success", "Order deleted successfully");
            closeDialog();
          })
          .catch(() => {
            hintPrompt("error", "Failed to delete order");
          })
          .finally(() => {
            setLoading(false);
          });
      },
      [hintPrompt, closeDialog],
    );

    return (
      <Modal
        className={className}
        title={`Delete Order ${customerOrderNo}`}
        closable={{ "aria-label": "Custom Close Button" }}
        open={isModalOpen}
        footer={null}
        onCancel={closeDialog}
        maskClosable={false}
        loading={loading}
      >
        {existingCubeOrder ? (
          <div>
            {
              <Alert
                message={
                  deletable
                    ? "This order has a cube created, but refunded!"
                    : "This order has a cube created, please delete the cube order first."
                }
                type={
                  deletable ? "success" : "warning"
                }
                showIcon
              />
            }
            <div>
              <div className="flex items-center gap-2">
                <Indicator label="Cube Order" />
                <Button size="small" type="link" icon={<CopyOutlined />} onClick={() => {
                  if ('instruction' in existingCubeOrder) {
                    copyText(existingCubeOrder.instruction.instructionNumber);
                  } else {
                    copyText(existingCubeOrder.deliverNumber);
                  }
                }}>
                  Copy Order No.
                </Button>
              </div>
              <Button
                size="small"
                type="link"
                disabled={!existingCubeOrder}
                icon={<LinkOutlined />}
                onClick={() => {
                  if ('instruction' in existingCubeOrder) {
                    window.open(
                      `http://oms.riiexpress.com/invoiceHistoryInf/${existingCubeOrder.instruction.instructionId}`,
                      "_blank",
                    );
                  } else {
                    window.open(
                      `http://oms.riiexpress.com/bigOutsDetails/${existingCubeOrder.boxStockDeliverId}`,
                      "_blank",
                    );
                  }
                }}
              >
                {'instruction' in existingCubeOrder
                  ? `${existingCubeOrder.instruction.instructionNumber} (${existingCubeOrder.instruction.state})`
                  : existingCubeOrder.deliverNumber}
              </Button>
            </div>
          </div>
        ) : (
          <Alert
            message="Are you sure you want to delete this order?"
            type="warning"
            showIcon
          />
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button
            type="primary"
            danger
            onClick={() => handleOk(orderIdRef.current, true)}
            disabled={!deletable}
          >
            Delete Forever
          </Button>
          <Button
            type="primary"
            color="default"
            onClick={() => handleOk(orderIdRef.current, false)}
            disabled={!deletable}
          >
            Mark as Deleted
          </Button>
          <Button onClick={closeDialog}>Cancel</Button>
        </div>
      </Modal>
    );
  },
);
